import type { ReactNode } from 'react';

/** Encabezado de pantalla: rotulo, titulo y una linea que dice para que sirve. */
export function Pagina({
  rotulo,
  titulo,
  ayuda,
  acciones,
  children,
}: {
  rotulo: string;
  titulo: string;
  ayuda?: string | undefined;
  acciones?: ReactNode | undefined;
  children: ReactNode;
}) {
  return (
    <>
      <div className="pagina__encabezado">
        <div className="fila" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p className="rotulo">{rotulo}</p>
            <h1 className="pagina__titulo">{titulo}</h1>
          </div>
          {acciones}
        </div>
        {ayuda ? <p className="pagina__ayuda">{ayuda}</p> : null}
      </div>
      {children}
    </>
  );
}

export function Bloque({
  titulo,
  nota,
  acciones,
  sinRelleno = false,
  children,
}: {
  titulo: string;
  nota?: string | undefined;
  acciones?: ReactNode | undefined;
  sinRelleno?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <section className="bloque">
      <div className="bloque__cabecera">
        <div>
          <h2 className="bloque__titulo">{titulo}</h2>
          {nota ? (
            <p className="campo__ayuda" style={{ marginTop: 2 }}>
              {nota}
            </p>
          ) : null}
        </div>
        {acciones}
      </div>
      <div className={`bloque__cuerpo ${sinRelleno ? 'bloque__cuerpo--sin-relleno' : ''}`}>
        {children}
      </div>
    </section>
  );
}

export function Cifra({
  rotulo,
  valor,
  nota,
  tono,
}: {
  rotulo: string;
  valor: string | number;
  nota?: string | undefined;
  tono?: 'atencion' | 'alarma' | undefined;
}) {
  return (
    <div className={`cifra ${tono ? `cifra--${tono}` : ''}`}>
      <p className="rotulo">{rotulo}</p>
      <p className="cifra__valor">{valor}</p>
      {nota ? <p className="cifra__nota">{nota}</p> : null}
    </div>
  );
}

/**
 * Cabecera de columna. Escribirla como `{ texto, num: true }` alinea el titulo
 * a la derecha igual que sus numeros: si el titulo queda a la izquierda y los
 * valores a la derecha, la columna deja de leerse como una sola cosa.
 */
export type Cabecera = string | { texto: string; num: true };

/** Tabla con desplazamiento horizontal propio: la pagina nunca se desplaza de lado. */
export function Tabla({ cabeceras, children }: { cabeceras: Cabecera[]; children: ReactNode }) {
  return (
    <div className="tabla-marco">
      <table className="tabla">
        <thead>
          <tr>
            {cabeceras.map((cabecera, indice) => {
              const texto = typeof cabecera === 'string' ? cabecera : cabecera.texto;
              const num = typeof cabecera !== 'string';
              return (
                <th
                  key={`${texto}-${indice}`}
                  scope="col"
                  className={num ? 'tabla__num' : undefined}
                >
                  {texto}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** Barra proporcional. `fraccion` entre 0 y 1, o `null` si no hay con que calcularla. */
export function Barra({ fraccion, bajo = false }: { fraccion: number | null; bajo?: boolean }) {
  if (fraccion === null) {
    return (
      <div className="barra" title="Sin ofertas en el período">
        <div className="barra__relleno barra__relleno--sin-datos" style={{ width: '100%' }} />
      </div>
    );
  }
  return (
    <div className="barra">
      <div
        className={`barra__relleno ${bajo ? 'barra__relleno--bajo' : ''}`}
        style={{ width: `${Math.min(100, Math.max(0, fraccion * 100))}%` }}
      />
    </div>
  );
}
